import tailwindColors from 'tailwindcss/colors'

const CURATED_FAMILY_NAMES = [
  'slate',
  'gray',
  'red',
  'orange',
  'amber',
  'yellow',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'pink',
  'rose',
] as const

export type PaletteColorFamilyName = (typeof CURATED_FAMILY_NAMES)[number]

const NAMED_SINGLE_KEYS = [
  'inherit',
  'current',
  'transparent',
  'black',
  'white',
] as const

type NamedSingleKey = (typeof NAMED_SINGLE_KEYS)[number]

type TailwindShadeRecord = Record<string, string>

function isTailwindColorFamily(value: unknown): value is TailwindShadeRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isCuratedFamilyName(
  name: string,
): name is PaletteColorFamilyName {
  return (CURATED_FAMILY_NAMES as ReadonlyArray<string>).includes(name)
}

export interface PaletteSwatch {
  family: PaletteColorFamilyName
  shade: string
  value: string
}

export interface PaletteColorFamily {
  name: PaletteColorFamilyName
  swatches: Array<PaletteSwatch>
}

function buildSwatches(
  name: PaletteColorFamilyName,
  record: TailwindShadeRecord,
): Array<PaletteSwatch> {
  return Object.entries(record).map(([shade, value]) => ({
    family: name,
    shade,
    value,
  }))
}

const tailwindColorsTyped = tailwindColors as Record<string, unknown>

export const curatedPaletteFamilies: Array<PaletteColorFamily> =
  CURATED_FAMILY_NAMES.map((name) => {
    const record = tailwindColorsTyped[name]
    if (!isTailwindColorFamily(record)) {
      return { name, swatches: [] }
    }

    return { name, swatches: buildSwatches(name, record) }
  })

export const curatedPaletteSwatches: Array<PaletteSwatch> =
  curatedPaletteFamilies.flatMap((family) => family.swatches)

const valueToSwatchLookup = new Map<string, PaletteSwatch>()
for (const swatch of curatedPaletteSwatches) {
  valueToSwatchLookup.set(swatch.value, swatch)
}

export function findPaletteSwatch(value: string): PaletteSwatch | undefined {
  return valueToSwatchLookup.get(value)
}

export function isTailwindNamedSingleKey(key: string): key is NamedSingleKey {
  return (NAMED_SINGLE_KEYS as ReadonlyArray<string>).includes(key)
}
